// ParticlesBackground — minimalist animated network overlay used as the liqmap backdrop.
// Pure canvas, no deps. Self-pauses when tab is hidden.
import React, { useEffect, useRef } from "react";

export default function ParticlesBackground({ density = 0.00008, color = "rgba(168,85,247,0.55)" }) {
  const ref = useRef(null);
  const rafRef = useRef(null);

  useEffect(() => {
    const cnv = ref.current;
    if (!cnv) return;
    const ctx = cnv.getContext("2d");
    if (!ctx) return;

    let w = 0, h = 0, dpr = window.devicePixelRatio || 1;
    let dots = [];

    const seed = () => {
      const n = Math.max(40, Math.min(220, Math.floor(w * h * density)));
      dots = Array.from({ length: n }, () => ({
        x: Math.random() * w,
        y: Math.random() * h,
        vx: (Math.random() - 0.5) * 0.25,
        vy: (Math.random() - 0.5) * 0.25,
      }));
    };

    const fit = () => {
      const r = cnv.getBoundingClientRect();
      w = r.width; h = r.height;
      cnv.width = w * dpr; cnv.height = h * dpr;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      seed();
    };

    const tick = () => {
      ctx.clearRect(0, 0, w, h);
      const linkDist = 110;
      for (const d of dots) {
        d.x += d.vx; d.y += d.vy;
        if (d.x < 0 || d.x > w) d.vx = -d.vx;
        if (d.y < 0 || d.y > h) d.vy = -d.vy;
      }
      for (let i = 0; i < dots.length; i++) {
        const a = dots[i];
        for (let j = i + 1; j < dots.length; j++) {
          const b = dots[j];
          const dx = a.x - b.x, dy = a.y - b.y;
          const dist = Math.sqrt(dx * dx + dy * dy);
          if (dist < linkDist) {
            ctx.beginPath();
            ctx.strokeStyle = color.replace(/,[^,]+\)$/, `,${(1 - dist / linkDist) * 0.35})`);
            ctx.lineWidth = 0.5;
            ctx.moveTo(a.x, a.y);
            ctx.lineTo(b.x, b.y);
            ctx.stroke();
          }
        }
      }
      for (const d of dots) {
        ctx.beginPath();
        ctx.fillStyle = color;
        ctx.arc(d.x, d.y, 1.2, 0, Math.PI * 2);
        ctx.fill();
      }
      rafRef.current = requestAnimationFrame(tick);
    };

    const ro = new ResizeObserver(fit);
    ro.observe(cnv);
    fit();
    rafRef.current = requestAnimationFrame(tick);

    const onVis = () => {
      if (document.hidden) cancelAnimationFrame(rafRef.current);
      else rafRef.current = requestAnimationFrame(tick);
    };
    document.addEventListener("visibilitychange", onVis);

    return () => {
      cancelAnimationFrame(rafRef.current);
      ro.disconnect();
      document.removeEventListener("visibilitychange", onVis);
    };
  }, [density, color]);

  return <canvas ref={ref} className="absolute inset-0 h-full w-full" style={{ pointerEvents: "none" }} aria-hidden />;
}
